import { useState } from "react";
import { Breadcrumbs, DateTimeDisplay, StatusMessage } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import { ModalContent } from "../../components/ui/ModalContent";
import { useAuth0 } from "../../contexts/useAuthContext";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { CategoryEditor } from "../../features/categories/CategoryEditor";
import { CategoryList } from "../../features/categories/CategoryList";
import { hasAssignedSites } from "../../features/categories/categoryCustomers";
import { CATEGORY_DELETE_BLOCKED, useCategories } from "../../features/categories/useCategories";
import type { CategorySummary } from "../../services/category/category.types";

const Categories = () => {
    const data = useCategories();
    const { hasRole } = useAuth0();
    const canManage = hasRole("Admin");
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: "Categories" });
    const [editor, setEditor] = useState<{ category: CategorySummary | null } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<CategorySummary | null>(null);
    const openEditor = (category: CategorySummary | null) => {
        if (!canManage) return;
        data.setError(null);
        setEditor({ category });
    };
    const openDelete = (category: CategorySummary) => {
        if (!canManage) return;
        data.setError(null);
        data.setModalError(null);
        if (hasAssignedSites(category)) data.setError(CATEGORY_DELETE_BLOCKED);
        else setDeleteTarget(category);
    };
    const saved = async (category: CategorySummary, warning?: string) => {
        const editing = !!editor?.category;
        setEditor(null);
        await data.saved(category, editing, warning);
    };
    const remove = async () => {
        if (!canManage) throw new Error("You do not have permission to manage categories");
        if (data.submitting) return;
        if (deleteTarget && await data.remove(deleteTarget)) setDeleteTarget(null);
    };
    return (
        <main>
            <div className="container mb-2">
                {editor && <CategoryEditor category={editor.category} onSaved={saved} onCancel={() => setEditor(null)} />}
                <Modal isOpen={deleteTarget !== null} title="Delete Category" headerClass="modal__header--danger"
                    okButtonLoading={data.submitting} cancelButtonLoading={data.submitting}
                    content={<>
                        <ModalContent heading="" message={`Are you sure you want to delete "${deleteTarget?.name}"?`} />
                        {data.modalError && <StatusMessage kind="error" onClose={() => data.setModalError(null)}>{data.modalError}</StatusMessage>}
                    </>}
                    onConfirm={remove} onCancel={() => { if (!data.submitting) setDeleteTarget(null); }} />
                <section className="page">
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--category">Classification Categories</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>{canManage && !editor && <button type="button" className="btn btn-add-new"
                            disabled={data.loading || data.submitting || !!deleteTarget} onClick={() => openEditor(null)}>Add Category</button>}</div>
                    </div>
                </section>
                {data.success && <StatusMessage kind="success" onClose={() => data.setSuccess(null)}>{data.success.message}</StatusMessage>}
                {data.error && <StatusMessage kind="error" onClose={() => data.setError(null)}>{data.error}</StatusMessage>}
                <section className="page">
                    <div className="alarm-content">
                        <CategoryList categories={data.categories} loading={data.loading} canManage={canManage}
                            busy={data.submitting || !!editor || !!deleteTarget} onEdit={openEditor} onDelete={openDelete} />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default Categories;
